import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import jsQR from 'jsqr';
import api from '../api';
import './DashboardSettingsModal.css';

export default function DashboardSettingsModal({
  dashboard,
  dashboards = [],
  onClose,
  onChanged,
  onSelectDashboard,
}) {
  const [selectedId, setSelectedId] = useState(dashboard?.id || '');
  const activeDash =
    dashboards.find((d) => d.id === selectedId) || dashboard || null;

  const [code, setCode] = useState(activeDash?.invite_code || '');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const isOwner = activeDash?.role === 'owner';
  const dashName = activeDash?.name || 'Dashboard';
  const showPicker = (dashboards?.length || 0) > 1;

  useEffect(() => {
    setCode(activeDash?.invite_code || '');
  }, [activeDash?.invite_code]);

  useEffect(() => {
    if (!activeDash?.id) return;
    let cancelled = false;
    setStats(null);
    setStatsLoading(true);
    api.get(`/api/dashboards/${activeDash.id}/stats`)
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [activeDash?.id]);

  useEffect(() => {
    setConfirmDelete(false);
  }, [selectedId]);

  const handlePickDashboard = (id) => {
    if (!id || id === selectedId) return;
    setSelectedId(id);
    if (onSelectDashboard) onSelectDashboard(id);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const flash = (msg, kind = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      flash('Code copied');
    } catch {
      flash('Copy failed', 'err');
    }
  };

  const regenerate = async () => {
    if (!isOwner || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/dashboards/${activeDash.id}/invite`);
      setCode(data.invite_code);
      flash('New code generated');
      onChanged && onChanged();
    } catch (err) {
      flash(err.response?.data?.error || 'Regenerate failed', 'err');
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    const trimmed = (joinCode || '').trim().toUpperCase();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/api/dashboards/join', { invite_code: trimmed });
      flash(`Joined "${data.name}"`);
      setJoinCode('');
      onChanged && onChanged();
      if (data?.id && onSelectDashboard) {
        setTimeout(() => onSelectDashboard(data.id), 300);
      }
      setTimeout(() => onClose && onClose(), 800);
    } catch (err) {
      flash(err.response?.data?.error || 'Could not join', 'err');
    } finally {
      setBusy(false);
    }
  };

  const handleScanFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });
      const max = 800;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const result = jsQR(imageData.data, w, h);
      if (!result || !result.data) {
        flash('No QR found in image', 'err');
        return;
      }
      const decoded = result.data.trim().toUpperCase();
      setJoinCode(decoded);
      flash('Code scanned — tap Join');
    } catch {
      flash('Could not read image', 'err');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/api/dashboards/${activeDash.id}`);
      flash(data?.left ? 'Left dashboard' : 'Dashboard deleted');
      onChanged && onChanged();
      setTimeout(() => onClose && onClose(), 600);
    } catch (err) {
      flash(err.response?.data?.error || 'Delete failed', 'err');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dsm-backdrop" onClick={onClose}>
      <div className="dsm-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="dsm-handle" />
        <div className="dsm-header">
          <div className="dsm-header-main">
            <div className="dsm-eyebrow">Dashboard Settings</div>
            {showPicker ? (
              <div className="dsm-picker">
                <select
                  className="dsm-picker-select"
                  value={selectedId}
                  onChange={(e) => handlePickDashboard(e.target.value)}
                  aria-label="Select dashboard"
                >
                  {dashboards.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}{d.role === 'owner' ? '' : ` · ${d.role || 'member'}`}
                    </option>
                  ))}
                </select>
                <span className="dsm-picker-caret" aria-hidden>▾</span>
              </div>
            ) : (
              <div className="dsm-title">{dashName}</div>
            )}
            <div className="dsm-role">{isOwner ? 'Owner' : (activeDash?.role || 'Member')}</div>
          </div>
          <button className="dsm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {toast && <div className={`dsm-toast dsm-toast-${toast.kind}`}>{toast.msg}</div>}

        <div className="dsm-section dsm-section-share">
          <div className="dsm-section-title">Share this dashboard</div>
          <div className="dsm-section-sub">Anyone with the code below can join and collaborate.</div>

          {code ? (
            <div className="dsm-qr-wrap">
              <div className="dsm-qr">
                <QRCodeSVG value={code} size={156} bgColor="#ffffff" fgColor="#0f1226" includeMargin={false} />
              </div>
              <div className="dsm-qr-actions">
                <div className="dsm-code-row">
                  <div className="dsm-code">{code}</div>
                  <button className="dsm-btn dsm-btn-ghost" onClick={copyCode} disabled={busy}>Copy</button>
                </div>
                {isOwner && (
                  <button className="dsm-link" onClick={regenerate} disabled={busy}>
                    Generate new code
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="dsm-empty">No invite code yet.{isOwner && (
              <button className="dsm-link" onClick={regenerate} disabled={busy}> Generate one</button>
            )}</div>
          )}
        </div>

        <div className="dsm-section dsm-section-join">
          <div className="dsm-section-title">Join another dashboard</div>
          <div className="dsm-section-sub">Enter or scan a code shared by someone else.</div>
          <div className="dsm-join-row">
            <input
              className="dsm-input"
              type="text"
              placeholder="ABCD1234"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={16}
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button className="dsm-btn dsm-btn-primary" onClick={join} disabled={busy || !joinCode.trim()}>
              Join
            </button>
          </div>
          <button
            className="dsm-scan"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={busy}
            type="button"
          >
            <span className="dsm-scan-icon">📷</span> Scan QR from photo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => handleScanFile(e.target.files && e.target.files[0])}
          />
        </div>

        <div className="dsm-section dsm-danger">
          <div className="dsm-section-title">Danger zone</div>
          {!confirmDelete ? (
            <button className="dsm-btn dsm-btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
              {isOwner ? 'Delete dashboard' : 'Leave dashboard'}
            </button>
          ) : (
            <div className="dsm-confirm">
              <div className="dsm-confirm-msg">
                {isOwner ? (
                  <>
                    <div className="dsm-confirm-head">⚠️ Delete "{dashName}" permanently?</div>
                    <div className="dsm-confirm-body">
                      This will erase{' '}
                      {statsLoading ? (
                        <span className="dsm-confirm-loading">counting…</span>
                      ) : stats ? (
                        <>
                          <strong className="dsm-confirm-count">
                            {stats.transactions} transaction{stats.transactions === 1 ? '' : 's'}
                          </strong>
                          {(stats.accounts || stats.scheduled_transactions || stats.split_groups || stats.tags) ? ', plus ' : ''}
                          {[
                            stats.accounts ? `${stats.accounts} account${stats.accounts === 1 ? '' : 's'}` : null,
                            stats.scheduled_transactions ? `${stats.scheduled_transactions} scheduled payment${stats.scheduled_transactions === 1 ? '' : 's'}` : null,
                            stats.split_groups ? `${stats.split_groups} split group${stats.split_groups === 1 ? '' : 's'}` : null,
                            stats.tags ? `${stats.tags} tag${stats.tags === 1 ? '' : 's'}` : null,
                          ].filter(Boolean).join(', ')}
                          .
                        </>
                      ) : (
                        <>all transactions and related data.</>
                      )}{' '}
                      <strong>This cannot be undone.</strong>
                    </div>
                  </>
                ) : (
                  <div className="dsm-confirm-body">You will lose access to this shared dashboard. Continue?</div>
                )}
              </div>
              <div className="dsm-confirm-row">
                <button className="dsm-btn dsm-btn-ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>
                  Cancel
                </button>
                <button className="dsm-btn dsm-btn-danger" onClick={doDelete} disabled={busy}>
                  {isOwner ? 'Yes, delete everything' : 'Yes, leave'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
